import { FC } from 'react';
import { Modal, FileInput } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps, useModalContext } from 'store/modal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { useImportShopItems } from 'store/admin-shop';
import { usePredefinedToasts } from 'utils/Toast';
import { FileImportErrorsModal } from 'components/modals';

interface Values {
  file: File | undefined;
}

interface Props extends ModalProps {}

export const ShopItemsImportModal: FC<Props> = ({ closeModal }) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const { openModal } = useModalContext();

  const { control, handleSubmit } = useForm<Values>({
    defaultValues: { file: undefined },
    mode: 'onChange',
  });

  const { mutate: importShopItems, status } = useImportShopItems();

  const onSubmit: SubmitHandler<Values> = (values) => {
    if (values.file) {
      importShopItems(
        { file: values.file },
        {
          onSuccess: ({ data }) => {
            closeModal({ action: 'CLOSE' });

            if (data.success) {
              predefinedToasts.success.added();
            } else {
              openModal({
                component: FileImportErrorsModal,
                size: 'lg',
                props: {
                  title: t('actions.import'),
                  onBack: () => openModal({ component: ShopItemsImportModal }),
                  errors: data.errorMessages.map((error) => ({
                    row: error.row,
                    field: error.column,
                    message: error.message,
                  })),
                },
              });
            }
          },
        },
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('actions.import')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('actions.import'),
            disabled: status === 'loading',
            type: 'submit',
          },
        ]}
      >
        <Controller
          name="file"
          control={control}
          render={({ field: { name, onChange, ref } }) => (
            <FileInput
              name={name}
              onChange={([file]) => {
                onChange(file);
              }}
              accept={['xls', 'xlsx']}
              maxFiles={1}
              required
              ref={ref}
            />
          )}
        />
      </Modal.Content>
    </form>
  );
};
